/**
 * Bundled imagery.
 *
 * Meal photographs travel with the app rather than over the network. Two
 * reasons: the demo build has to work with no server at all, and a meal card
 * with a blank image is the first thing anyone notices.
 *
 * A meal's `imageUrl` may therefore be any of:
 *
 *   `asset:chicken-pulao-dahi`  → the bundled photograph below
 *   `https://cdn…/meal.jpg`     → fetched as usual
 *   `null`                      → the neutral placeholder
 *
 * The server chooses. Seed data uses `asset:` references so a fresh install
 * looks right offline; a real deployment would return CDN URLs, and nothing
 * else in the app has to change.
 *
 * The photographs are cropped from the client's approved artwork
 * (`Requirements/Food_Donation_FINAL_CORRECTED_ALL_SCREENS.pdf`, p19), so the
 * app shows the same food the designs did.
 *
 * The photographs are JPEG, not PNG: they are photographs, and as PNG the five
 * of them came to 1.8 MB against 163 KB encoded properly — a difference the
 * APK and every web visitor pays for.
 *
 * Haleem and Biryani are the exception: the artwork never contained them, so
 * `scripts/derive-meal-images.sh` grades the nearest dish into place. They are
 * stand-ins and are marked as such — replace them with real photography before
 * this reaches donors, who are looking at a picture of the food they are
 * paying for.
 */

import type { ImageSourcePropType } from 'react-native';

const ASSET_PREFIX = 'asset:';

const MEAL_IMAGES: Record<string, ImageSourcePropType> = {
  'daal-roti-achar': require('../../assets/meals/daal-roti-achar.jpg'),
  'chana-rice-dahi': require('../../assets/meals/chana-rice-dahi.jpg'),
  'chicken-pulao-dahi': require('../../assets/meals/chicken-pulao-dahi.jpg'),
  haleem: require('../../assets/meals/haleem.jpg'),
  'biryani-with-raita': require('../../assets/meals/biryani-with-raita.jpg'),
};

/** Slug for a meal name, used to match seed data to a bundled photograph. */
export function mealSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\+/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    // "daal-2-roti-achar" → "daal-roti-achar": the count is not part of the id.
    .replace(/-\d+-/g, '-');
}

/**
 * Resolve a meal image to something `<Image source>` accepts.
 *
 * Returns undefined when there is nothing to show, so callers can render their
 * own placeholder rather than an empty box.
 */
export function mealImageSource(
  imageUrl: string | null | undefined,
  fallbackName?: string,
): ImageSourcePropType | undefined {
  if (imageUrl?.startsWith(ASSET_PREFIX)) {
    return MEAL_IMAGES[imageUrl.slice(ASSET_PREFIX.length)];
  }
  if (imageUrl) {
    return { uri: imageUrl };
  }
  // No image from the server: fall back to a bundled photograph if the meal
  // name matches one we ship. Better a correct picture than a grey rectangle.
  if (fallbackName) {
    return MEAL_IMAGES[mealSlug(fallbackName)];
  }
  return undefined;
}

export const BUNDLED_MEAL_SLUGS = Object.keys(MEAL_IMAGES);

/**
 * Home's illustrations: Microsoft's Fluent Emoji in their 3D style, MIT
 * licensed (`assets/illustrations/LICENSE.md`). Bundled images rather than
 * emoji text, because an emoji is drawn by whatever font the device has —
 * flat on Windows, different again on Android — and Home is where the look
 * matters most.
 */
export const ART = {
  checkMark: require('../../assets/illustrations/check-mark.png'),
  curryRice: require('../../assets/illustrations/curry-rice.png'),
  glowingStar: require('../../assets/illustrations/glowing-star.png'),
  greenSalad: require('../../assets/illustrations/green-salad.png'),
  herb: require('../../assets/illustrations/herb.png'),
  openBook: require('../../assets/illustrations/open-book.png'),
  palmUpHand: require('../../assets/illustrations/palm-up-hand.png'),
  palmsUp: require('../../assets/illustrations/palms-up.png'),
  receipt: require('../../assets/illustrations/receipt.png'),
  redHeart: require('../../assets/illustrations/red-heart.png'),
  shield: require('../../assets/illustrations/shield.png'),
  tiffinBox: require('../../assets/illustrations/tiffin-box.png'),
} satisfies Record<string, ImageSourcePropType>;

export type ArtName = keyof typeof ART;
